import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import {
  deliveryMethodService,
  DeliveryMethodDto,
  BulkUpdateDeliveryMethodRequest,
  LayoutLookupDto
} from '../../../../../services/deliveryMethodService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { DeliveryMethodSidePanel } from './DeliveryMethodSidePanel';
import { DeliveryMethodCreateModal } from './DeliveryMethodCreateModal';

interface DeliveryMethodDataTableProps {
  refreshTrigger?: number;
}

export const DeliveryMethodDataTable: React.FC<DeliveryMethodDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  const [data, setData] = useState<DeliveryMethodDto[]>([]);
  const [layouts, setLayouts] = useState<LayoutLookupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const inlineEditConfig = useInlineEdit<DeliveryMethodDto>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, editedData) => {
      const request: BulkUpdateDeliveryMethodRequest = {
        companyNo: originalRow.companyNo,
        deliveryMethodNo: (editedData.deliveryMethodNo as number) ?? originalRow.deliveryMethodNo,
        description: (editedData.description as string) ?? originalRow.description,
        externalID: (editedData.externalID as string) ?? originalRow.externalID,
        consignmentSWDefProductNo: (editedData.consignmentSWDefProductNo as string) ?? originalRow.consignmentSWDefProductNo,
        consignmentSWAdditionalCode: (editedData.consignmentSWAdditionalCode as string) ?? originalRow.consignmentSWAdditionalCode,
        shippingLabelsYesNo: (editedData.shippingLabelsYesNo as boolean) ?? originalRow.shippingLabelsYesNo,
        shippingLabelsLayoutNo: (editedData.shippingLabelsLayoutNo as number) ?? originalRow.shippingLabelsLayoutNo,
        labelExportFolder: (editedData.labelExportFolder as string) ?? originalRow.labelExportFolder,
        labelExportFilename: (editedData.labelExportFilename as string) ?? originalRow.labelExportFilename,
        packageSlipERPYesNo: (editedData.packageSlipERPYesNo as boolean) ?? originalRow.packageSlipERPYesNo,
        shippingLabelsERPYesNo: (editedData.shippingLabelsERPYesNo as boolean) ?? originalRow.shippingLabelsERPYesNo,
        consignmentNoteERPYesNo: (editedData.consignmentNoteERPYesNo as boolean) ?? originalRow.consignmentNoteERPYesNo,
        singleParcelDelivery: (editedData.singleParcelDelivery as boolean) ?? originalRow.singleParcelDelivery
      };
      await deliveryMethodService.bulkUpdate([request]);
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || 'Feil ved lagring')
  });

  useEffect(() => {
    loadData();
    deliveryMethodService.getLayouts().then(setLayouts).catch(() => {});
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const methods = await deliveryMethodService.getCurrent().catch(() =>
        deliveryMethodService.getAll().catch(() => [])
      );
      setData(methods);
    } catch (err: any) {
      console.error('Error loading delivery methods:', err);
      setError('Kunne ikke laste leveringsmåter. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  const columnFactory = createColumnFactory<DeliveryMethodDto>();

  const columns: ColumnDefinition<DeliveryMethodDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('deliveryMethodNo', 'deliveryMethodNo', 'Lev.måte nr.', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('description', 'description', 'Beskrivelse', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('externalID', 'externalID', 'Transportør ID', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('consignmentSWDefProductNo', 'consignmentSWDefProductNo', 'TA Produktnr.', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('consignmentSWAdditionalCode', 'consignmentSWAdditionalCode', 'TA Tilleggskode', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('shippingLabelsLayoutNo', 'shippingLabelsLayoutNo', 'Etikettlayout', {
      minWidth: 180,
      sortable: true,
      filterType: 'both',
      editable: true,
      visible: false,
      ...(layouts.length > 0 ? {
        editType: 'select' as const,
        editOptions: layouts.map(l => ({ value: l.layoutNo, label: `${l.layoutNo} - ${l.description || ''}` }))
      } : {}),
      formatter: (value: any) => {
        if (value == null) return '-';
        const layout = layouts.find(l => l.layoutNo === value);
        return layout ? `${value} - ${layout.description}` : String(value);
      }
    }),

    columnFactory.createTextColumn('labelExportFolder', 'labelExportFolder', 'Eksportkatalog', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true,
      visible: false
    }),

    columnFactory.createBooleanColumn('shippingLabelsYesNo', 'shippingLabelsYesNo', 'Fraktetikett', {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('singleParcelDelivery', 'singleParcelDelivery', 'Enkeltkolli', {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('packageSlipERPYesNo', 'packageSlipERPYesNo', 'Pakkseddel ERP', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('shippingLabelsERPYesNo', 'shippingLabelsERPYesNo', 'Etikett ERP', {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('consignmentNoteERPYesNo', 'consignmentNoteERPYesNo', 'Fraktbrev ERP', {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [layouts]);

  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert('Velg minst én leveringsmåte for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length]);

  const tableActions: TableAction<DeliveryMethodDto>[] = useMemo(() => [
    {
      id: 'create',
      label: 'Ny leveringsmåte',
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedIds.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit]);

  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'delivery-method-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {showBulkEditModal && selectedIds.length > 0 && (
        <DeliveryMethodSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {showCreateModal && (
        <DeliveryMethodCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
